import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DocumentNodeModel, FolderModel } from '../../../../core/models/data/document.model';
import { DocumentService } from '../../../../core/services/document.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { DocumentListSortOptions } from '../../../../core/enums/sort-options.enums';
import { ModalActions } from '../../../../core/enums/base.enums';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { DocumentsConstants } from '../../../../core/constants/documents.constants';
import { DocumentsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { FolderTreeDestination, FolderTreePickerComponent, FolderTreeSelection } from '../../../../parts/folder-tree-picker/folder-tree-picker.component';

@Component({
    selector: 'ms-document-move-folder-modal',
    standalone: true,
    imports: [FontAwesomeModule, EscCloseDirective, FolderTreePickerComponent],
    templateUrl: './document-move-folder-modal.component.html'
})
export class DocumentMoveFolderModalComponent {

    public sourceFolder = input<DocumentNodeModel | undefined>(undefined);
    public folderTree = input<DocumentNodeModel[]>([]);

    public submitted = output<FolderModel>();
    public cancelled = output<void>();

    public modalId = DocumentsConstants.MOVE_FOLDER_MODAL_ID();

    public selectedDestination = signal<FolderTreeSelection>(undefined);
    public isSubmitting = signal<boolean>(false);
    public errorMessage = signal<string>('');

    private documentService = inject(DocumentService);
    private bootstrapService = inject(BootstrapService);

    // Hide the source folder from the tree. Because the tree is navigated by expanding, hiding the
    // source makes its entire subtree unreachable too — so this single exclusion enforces the
    // "can't move a folder into itself or a descendant" rule without crawling the subtree. The API
    // is the final authority and rejects an invalid destination regardless.
    public excludedIds = computed<Set<number>>(() => {
        const src = this.sourceFolder();
        return src ? new Set<number>([src.documentNodeId]) : new Set<number>();
    });

    // Fetch a folder's child folders the first time the picker expands it (lazy — no full-tree crawl).
    public loadChildren = (folderId: number): Promise<DocumentNodeModel[]> =>
        this.documentService.getChildren(folderId, DocumentListSortOptions.NameAsc)
            .then(result => result.success ? (result.data ?? []) : []);

    // The source's current parent isn't a valid destination — moving it there is a no-op. At the
    // root that's the Root option (rootDisabled); otherwise it's the parent folder (disabledIds).
    public rootDisabled = computed<boolean>(() => this.sourceFolder()?.parentId == null);

    public disabledIds = computed<Set<number>>(() => {
        const currentParentId = this.sourceFolder()?.parentId;
        return currentParentId != null ? new Set<number>([currentParentId]) : new Set<number>();
    });

    public canSubmit = computed<boolean>(() => this.selectedDestination() !== undefined && !this.isSubmitting());

    constructor() {
        // Reset selection / error whenever the source folder changes (new modal open). The picker
        // collapses its tree when the selection clears.
        effect(() => {
            const _ = this.sourceFolder();
            this.selectedDestination.set(undefined);
            this.errorMessage.set('');
        });
    }

    public async submit(): Promise<void> {
        const src = this.sourceFolder();
        const dest = this.selectedDestination();
        if (!src || dest === undefined) return;

        const destinationParentId = dest === FolderTreeDestination.Root ? undefined : dest;

        this.isSubmitting.set(true);
        this.errorMessage.set('');

        const result = await this.documentService.moveFolder(src.documentNodeId, destinationParentId, src.name);
        this.isSubmitting.set(false);

        if (result.success && result.data) {
            this.submitted.emit(result.data);
            this.close();
            return;
        }

        this.errorMessage.set(result.message ?? DocumentsMessagingConstants.DOCUMENTS_MOVE_FAILED());
    }

    public cancel(): void {
        this.cancelled.emit();
        this.close();
    }

    public close(): void {
        this.bootstrapService.manageModal(`#${this.modalId}`, ModalActions.Hide);
    }

}
